// Copy for the browser terminal, ported verbatim from the ssh TUI's
// content.go (apps/trapeznikov-ssh) so web and CLI stay in lockstep.
// Plain "-" only, never em/en dashes.

export const VERSION = 'v0.2.0';

export const BANNER_LINE1 = "Hey! I'm Eugene,";
export const BANNER_LINE2 =
    'Senior machine learning engineer based in Seattle, working at Etsy. ' +
    'Welcome to my CLI portfolio. Pick from below to explore.';

export const MENU_ITEMS = [
    { id: 'whoami',   desc: 'who I am' },
    { id: 'work',     desc: 'work history' },
    { id: 'projects', desc: "what I've shipped" },
    { id: 'posts',    desc: 'things I write' },
    { id: 'contact',  desc: 'links & socials' },
    { id: 'exit',     desc: 'quit' },
];

export const WHOAMI_PARAGRAPHS = [
    "I'm a machine learning engineer who ships recommendation systems to 90M+ buyers at Etsy and builds autonomous agent infrastructure after hours.",
    'Before that - a decade of consumer products used by hundreds of millions: on-device speech ML for Amazon Alexa, personalization at Amazon, a 100M-user voice platform at Zello, and the most popular trucker app in the US.',
    "Outside of work I love exploring and collaborating. That's how I teamed up with Photo Lab and helped make it App Store and Google Play Best of 2017; nowadays it's LLMs and autonomous agents - contributing to shipwright (github.com/app-vitals/shipwright), an open-source AI software factory - and writing posts (trapeznikov.com/posts) about what I find.",
    'I also love meetups and conferences - swapping ideas and seeing what others are building. Catch me regularly at AI Tinkerers Seattle (seattle.aitinkerers.org).',
];

export const WORK_ROWS = [
    { years: '2022-now', company: 'etsy',         place: 'seattle',  line: 'senior machine learning engineer, recommendations - retrieval, ranking, real-time ML inference for 90M+ shoppers' },
    { years: '2018-22',  company: 'amazon',       place: 'seattle',  line: 'senior software engineer - ML infrastructure for personalization; shipped on-device speech models for Alexa Hands-Free' },
    { years: '2017-18',  company: 'zello',        place: 'austin',   line: 'software developer - real-time voice platform with 100M+ users' },
    { years: '2015-17',  company: 'trucker path', place: 'san jose', line: 'software developer - led mobile team through 100x user growth' },
];

export const RECS = [
    { name: 'woven bag',    score: '.94' },
    { name: 'floral throw', score: '.91' },
    { name: 'clay kit',     score: '.89' },
    { name: 'green lamp',   score: '.87' },
];

export const DAGS = [
    { name: 'ranking_train_daily',  mark: '✓', state: 'success', time: '14m',   kind: 'ok' },
    { name: 'embeddings_refresh',   mark: '●', state: 'running', time: '02:31', kind: 'run' },
    { name: 'feature_store_sync',   mark: '✓', state: 'success', time: '8m',    kind: 'ok' },
    { name: 'offline_eval_nightly', mark: '⧗', state: 'queued',  time: '--',    kind: 'wait' },
];

export const PROJECTS = [
    {
        title: 'etsy',
        badge: 'now',
        badgeKind: 'now',
        mock: 'recs',
        paragraphs: [
            "Sr. machine learning engineer on Etsy Recommendations. I build the path that takes recommendation models from a scientist's notebook to production serving 90+ million shoppers - candidate retrieval, real-time inference pipelines, and LLM-powered understanding of items and queries across 100M+ one-of-a-kind listings.",
        ],
        links: [{ label: 'link', url: 'etsy.com' }],
    },
    {
        title: 'amazon personalization',
        badge: '',
        badgeKind: '',
        mock: 'airflow',
        paragraphs: [
            'Sr. software engineer on Amazon Personalization - the team behind product recommendations across Amazon. MLOps at scale: migrated 100+ ML and data pipelines to Apache Airflow, introduced a feature store and automated model evaluation, and cut model training time by 20% to keep recommendations fresh on real-time trends.',
        ],
        links: [{ label: 'link', url: 'amazon.com' }],
    },
    {
        title: 'amazon alexa',
        badge: '',
        badgeKind: '',
        mock: '',
        paragraphs: [
            'Key developer on Alexa Hands-Free - always-on, on-device speech recognition, built with Qualcomm and Motorola silicon teams to run the audio ML engine at the edge.',
            "I also shipped Amazon's own voice-recognition mobile solution and a privacy-preserving federated learning system.",
        ],
        links: [
            { label: 'google play', url: 'play.google.com/store/apps/details?id=com.amazon.dee.app' },
            { label: 'app store', url: 'itunes.apple.com/us/app/amazon-alexa/id944011620' },
        ],
    },
    {
        title: 'photo lab',
        badge: 'collaboration',
        badgeKind: 'collab',
        mock: '',
        paragraphs: [
            '🏆 App Store Best of 2017    🏆 Google Play Best of 2017',
            'Full rebrand and customer-experience overhaul I drove while leading the mobile team, working with scientists to ship image style transfer to 120+ million users.',
        ],
        links: [
            { label: 'google play', url: 'play.google.com/store/apps/details?id=vsin.t16_funny_photo' },
            { label: 'app store', url: 'itunes.apple.com/us/app/id441457218' },
        ],
    },
    {
        title: 'trucker path',
        badge: '',
        badgeKind: '',
        mock: '',
        paragraphs: [
            'The most popular app for truckers in the US.',
            'I led mobile development as we grew from a few thousand users to 700,000 - spending as much time talking to truckers about what mattered to them as shipping it.',
        ],
        links: [
            { label: 'google play', url: 'play.google.com/store/apps/details?id=com.sixdays.truckerpath' },
            { label: 'app store', url: 'itunes.apple.com/US/app/id782746890' },
        ],
    },
];

// Mirrors the vendored blog catalog, newest-first. scripts/check-post-sync.mjs
// guards this browser copy and the SSH copy against missing future posts.
export const POSTS = [
    { title: 'Freeing 10% of Your Agent\'s Context with Jev', url: 'https://trapeznikov.com/posts/freeing-10-of-your-agents-context-with-jev' },
    { title: 'Proactive Recall for Coding Agents: Why Memory Belongs in the Harness', url: 'https://trapeznikov.com/posts/proactive-recall-for-coding-agents' },
    { title: 'Make Your Agent Workflows Harness-Agnostic', url: 'https://trapeznikov.com/posts/make-your-agent-workflows-harness-agnostic' },
    { title: 'MCP Profiles: The Layer Between User and Repo Config', url: 'https://trapeznikov.com/posts/mcp-profiles-the-layer-between-user-and-repo-config' },
    { title: 'Advanced Animations with UIViewPropertyAnimator', url: 'https://trapeznikov.com/posts/advanced-animations-with-uiviewpropertyanimator' },
    { title: 'Building a Geo Targeting iOS App in Swift', url: 'https://trapeznikov.com/posts/building-a-geo-targeting-ios-app-in-swift' },
    { title: 'How to Use Xcode Targets to Manage Development and Production Builds', url: 'https://trapeznikov.com/posts/how-to-use-xcode-targets-to-manage-development-and-production-builds' },
];

export const CONTACTS = [
    { label: 'e@trapeznikov.com', url: 'mailto:e@trapeznikov.com' },
    { label: 'github.com/EugeneTrapeznikov', url: 'https://github.com/EugeneTrapeznikov' },
    { label: 'linkedin.com/in/eugenetrapeznikov', url: 'https://www.linkedin.com/in/eugenetrapeznikov/' },
    { label: 'trapeznikov.com', url: 'https://trapeznikov.com' },
];
